'use client';

import { useState } from 'react';
import { motion } from 'motion/react';

import { Text } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

import { cn } from '../lib';

export interface ITabItem {
  id: string;
  label: TTextLocalized;
}

interface ITabbedButtonGroupProps {
  tabs: ITabItem[];
  onSwitchTab?: (tab: ITabItem) => void;
}

/**
 * -----------------------------------------------------------------------------
 * This renders a tabbed list that can switch between different views.
 * TODO: Later allow this to allow control the tabbed sibling content view.
 */
export function TabbedButtonGroup({
  tabs,
  onSwitchTab,
}: ITabbedButtonGroupProps) {
  const initTab = tabs?.[0]?.id;

  const [activeTab, setActiveTab] = useState(initTab);

  const handleOnSwitchTab = (tab: ITabItem) => () => {
    setActiveTab(tab.id);
    onSwitchTab?.(tab);
  };

  return (
    <div
      className="
        flex shrink-0 gap-1 rounded-lg border border-gray-200 border-gray-950/20
        p-0.5 dark:border-white/10 dark:bg-gray-400/10 dark:hover:border-white/20
      "
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={cn(
            `
            shadow-xs outline-primary-600/50 dark:outline-primary-300/80 relative
            z-20 cursor-pointer rounded-md border-[0.5] border-gray-200/50 px-4
            py-1 text-sm text-gray-900 ring-1 ring-inset ring-gray-100/50
            transition duration-700 focus-visible:outline-1
            `,
            activeTab === tab.id
              ? `
              hover:text-primary-100 dark:border-primary-500/80 font-medium
            text-white`
              : `
              hover:text-primary-800 hover:bg-primary-100 hover:border-primary-200
              dark:hover:border-primary-500/50 dark:hover:text-primary-300
              dark:hover:bg-primary-950/20 dark:border-gray-200/5 dark:bg-gray-900/5
              dark:text-white dark:ring-gray-100/5 dark:hover:border-gray-50/10
              `
          )}
          onClick={handleOnSwitchTab(tab)}
          style={{
            WebkitTapHighlightColor: 'transparent',
          }}
          type="button"
        >
          {activeTab === tab.id ? (
            <motion.span
              className="
                bg-primary-600 shadow-xs hover:bg-primary-900 dark:bg-primary-800
                dark:hover:bg-primary-500 absolute inset-0 -z-10 rounded-md
              "
              layoutId="bubble"
              transition={{ type: 'spring', bounce: 0.35, duration: 0.6 }}
            />
          ) : null}

          <Text className="pointer-events-none z-10" text={tab.label} />
        </button>
      ))}
    </div>
  );
}
